import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatSaturdayShort, formatTimeRange } from "../lib/fmt";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Truck, ClipboardList, PackageOpen,
  ArrowLeft, ArrowRight, CheckCircle2,
  CalendarPlus, ExternalLink, AlertTriangle,
} from "lucide-react";

const ROLE_META = {
  delivery_driver: { label: "Delivery Driver", icon: Truck, desc: "Drive deliveries to families" },
  delivery_coordinator: { label: "Delivery Coordinator", icon: ClipboardList, desc: "Lead the route & paperwork" },
  pickup_driver: { label: "Pick Up Driver", icon: PackageOpen, desc: "Pick up donations from warehouse" },
};

function RoleStep({ board, mySignupShiftIds, onPick }) {
  // Show TOTAL open capacity per role across upcoming Saturdays — independent
  // of whether the current user is already signed up on those days (the
  // one-role-per-Saturday guard is applied on the next step's date picker).
  const counts = useMemo(() => {
    const out = { delivery_driver: 0, delivery_coordinator: 0, pickup_driver: 0 };
    if (!board?.days) return out;
    for (const day of board.days) {
      const dow = new Date(day.date + "T12:00:00").getDay();
      if (dow !== 6) continue;
      for (const s of day.shifts) {
        for (const r of Object.keys(out)) {
          if (s.roles?.[r]?.open > 0) out[r] += 1;
        }
      }
    }
    return out;
  }, [board]);

  return (
    <div className="space-y-3 mt-2">
      <p className="text-sm text-slate-600">
        Pick the role you want to volunteer for. Next you&apos;ll choose which Saturdays you can make.
      </p>
      <div className="grid gap-3">
        {Object.entries(ROLE_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const available = counts[key];
          const disabled = available === 0;
          return (
            <button
              key={key}
              data-testid={`bulk-role-${key}`}
              disabled={disabled}
              onClick={() => onPick(key)}
              className={`text-left rounded-xl border-2 p-4 flex items-center gap-4 transition-all focus-ring ${
                disabled
                  ? "border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed"
                  : "border-slate-200 hover:border-brand-blue hover:bg-brand-blue/5 active:scale-[0.99]"
              }`}
            >
              <div className={`p-3 rounded-lg shrink-0 ${disabled ? "bg-slate-200 text-slate-400" : "bg-brand-blue/10 text-brand-blue"}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading text-base font-semibold text-slate-900">{meta.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{meta.desc}</div>
              </div>
              <div className="shrink-0 text-right">
                <div className={`text-lg font-bold leading-none ${disabled ? "text-slate-400" : "text-brand-blue"}`}>
                  {available}
                </div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mt-1">
                  {available === 1 ? "open date" : "open dates"}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateStep({ board, mySignupShiftIds, role, selected, setSelected, onBack, onNext }) {
  // Flatten shifts with role open into a list — one row per Saturday where the chosen role has open slots.
  const candidates = useMemo(() => {
    if (!board?.days) return [];
    const out = [];
    for (const day of board.days) {
      // Saturday-only — the org runs on Saturdays. Stray weekday shifts (from
      // admin testing) shouldn't appear in this bulk picker.
      const dow = new Date(day.date + "T12:00:00").getDay();
      if (dow !== 6) continue;
      for (const s of day.shifts) {
        if (mySignupShiftIds.has(s.id)) continue;
        const r = s.roles?.[role];
        if (!r || r.open <= 0) continue;
        out.push({
          id: s.id,
          date: s.date,
          start_time: s.start_time,
          end_time: s.end_time,
          route_name: s.route?.name || "Route",
          pickup_location: s.pickup_location,
        });
      }
    }
    return out;
  }, [board, mySignupShiftIds, role]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allChecked = candidates.length > 0 && candidates.every((c) => selected.has(c.id));
  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(candidates.map((c) => c.id)));
  };

  return (
    <div className="space-y-3 mt-2">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          Signing up as <span className="font-semibold text-slate-900">{ROLE_META[role].label}</span>.
          Pick every Saturday you can make.
        </p>
      </div>

      {candidates.length === 0 ? (
        <div className="text-center py-8 px-4 rounded-xl border border-dashed border-slate-300 bg-slate-50">
          <p className="text-sm text-slate-600">
            No open Saturdays for this role right now. Try a different role.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <Checkbox
                data-testid="bulk-select-all"
                checked={allChecked}
                onCheckedChange={toggleAll}
              />
              <span className="text-sm font-semibold text-slate-700">Select all ({candidates.length})</span>
            </label>
            <span className="text-xs font-bold uppercase tracking-wider text-brand-blue">
              {selected.size} selected
            </span>
          </div>

          <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1 space-y-2">
            {candidates.map((c) => {
              const checked = selected.has(c.id);
              return (
                <label
                  key={c.id}
                  data-testid={`bulk-date-row-${c.id}`}
                  className={`flex items-center gap-3 rounded-lg border-2 px-3 py-2.5 cursor-pointer transition-colors ${
                    checked ? "border-brand-blue bg-brand-blue/5" : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <Checkbox
                    data-testid={`bulk-date-cb-${c.id}`}
                    checked={checked}
                    onCheckedChange={() => toggle(c.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 text-sm">
                      {formatSaturdayShort(c.date)}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {c.route_name} · {formatTimeRange(c.start_time, c.end_time)}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </>
      )}

      <div className="flex items-center justify-between pt-2 gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          data-testid="bulk-back-to-role"
          className="min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Change role
        </Button>
        <Button
          type="button"
          onClick={onNext}
          disabled={selected.size === 0}
          data-testid="bulk-next-to-confirm"
          className="min-h-[44px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
        >
          Review {selected.size > 0 ? `(${selected.size})` : ""} <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

function ConfirmStep({ role, selectedDates, user, onBack, onSubmit, submitting }) {
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [smsOptIn, setSmsOptIn] = useState(true);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
    setPhone(user?.phone || "");
  }, [user]);

  const submit = (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim()) {
      return toast.error("Please fill out all contact fields.");
    }
    onSubmit({ name: name.trim(), email: email.trim(), phone: phone.trim(), sms_opt_in: smsOptIn });
  };

  return (
    <form onSubmit={submit} className="space-y-4 mt-2">
      <div className="rounded-lg border border-brand-blue/30 bg-brand-blue/5 p-3 text-sm">
        <div className="font-semibold text-slate-900">
          {ROLE_META[role].label} · {selectedDates.length} {selectedDates.length === 1 ? "Saturday" : "Saturdays"}
        </div>
        <div className="text-xs text-slate-600 mt-1 leading-relaxed">
          {selectedDates.map((d) => formatSaturdayShort(d)).join(" · ")}
        </div>
      </div>
      <p className="text-xs text-slate-500">
        Pre-filled from your profile — edit if you&apos;d like a different contact on file for these shifts.
      </p>
      <div>
        <Label htmlFor="bulk-name" className="text-sm font-semibold">Full name</Label>
        <Input id="bulk-name" data-testid="bulk-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 text-base min-h-[44px]" />
      </div>
      <div>
        <Label htmlFor="bulk-email" className="text-sm font-semibold">Email</Label>
        <Input id="bulk-email" data-testid="bulk-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 text-base min-h-[44px]" />
      </div>
      <div>
        <Label htmlFor="bulk-phone" className="text-sm font-semibold">Mobile phone</Label>
        <Input id="bulk-phone" data-testid="bulk-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="mt-1.5 text-base min-h-[44px]" />
      </div>
      <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2.5 transition-colors focus-within:ring-2 focus-within:ring-brand-blue">
        <input
          type="checkbox"
          data-testid="bulk-sms-optin"
          checked={smsOptIn}
          onChange={(e) => setSmsOptIn(e.target.checked)}
          className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-brand-blue"
        />
        <span className="text-sm text-slate-700 leading-snug">
          <span className="font-semibold text-slate-900">Also text me Saturday reminders</span>
          <br />
          <span className="text-xs text-slate-500">Confirmations + Wednesday reminders for all {selectedDates.length} Saturdays. Reply STOP any time.</span>
        </span>
      </label>

      <div className="flex items-center justify-between pt-2 gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          data-testid="bulk-back-to-dates"
          disabled={submitting}
          className="min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Change dates
        </Button>
        <Button
          type="submit"
          data-testid="bulk-submit-button"
          disabled={submitting}
          className="min-h-[44px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
        >
          {submitting ? "Signing up…" : `Confirm ${selectedDates.length} ${selectedDates.length === 1 ? "slot" : "slots"}`}
        </Button>
      </div>
    </form>
  );
}

function ResultStep({ result, onClose, onBackToTop }) {
  const claimed = result?.claimed || [];
  const skipped = result?.skipped || [];
  return (
    <div className="space-y-4 mt-2" data-testid="bulk-result">
      <div className="text-center">
        <div className="mx-auto h-14 w-14 rounded-full bg-brand-green/15 text-brand-green-dark flex items-center justify-center mb-2">
          <CheckCircle2 className="w-7 h-7" />
        </div>
        <p className="font-heading text-2xl text-slate-900 font-semibold">
          You&apos;re on for {claimed.length} {claimed.length === 1 ? "Saturday" : "Saturdays"}!
        </p>
        <p className="text-sm text-slate-600 mt-1.5">
          We just emailed you a confirmation — no need to save the links here.
        </p>
        {skipped.length > 0 && (
          <p className="text-xs text-amber-700 mt-1">{skipped.length} skipped — see below</p>
        )}
      </div>

      {claimed.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Your new Saturdays</p>
          <div className="max-h-[35vh] overflow-y-auto space-y-2">
            {claimed.map((c) => (
              <div
                key={c.signup_id}
                data-testid={`bulk-claimed-${c.signup_id}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-brand-green/40 bg-brand-green/5 px-3 py-2"
              >
                <span className="text-sm font-semibold text-slate-900">
                  {formatSaturdayShort(c.date)}
                </span>
                <div className="flex items-center gap-1.5">
                  <a
                    href={`${API_BASE}/public/signups/${c.edit_token}/calendar.ics`}
                    data-testid={`bulk-ics-${c.signup_id}`}
                    className="inline-flex items-center text-[11px] font-semibold text-brand-green-dark px-2 py-1 rounded-md border border-brand-green/40 bg-white hover:bg-brand-green/10"
                  >
                    <CalendarPlus className="w-3 h-3 mr-1" /> .ics
                  </a>
                  <Link
                    to={`/my-signup/${c.edit_token}`}
                    className="inline-flex items-center text-[11px] font-semibold text-brand-blue px-2 py-1 rounded-md border border-brand-blue/30 bg-white hover:bg-brand-blue/10"
                  >
                    <ExternalLink className="w-3 h-3 mr-1" /> Manage
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Skipped
          </p>
          <div className="space-y-1.5">
            {skipped.map((s, i) => (
              <div
                key={`${s.shift_id}-${i}`}
                data-testid={`bulk-skipped-${s.shift_id}`}
                className="flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs"
              >
                <span className="font-semibold text-slate-700">
                  {s.date ? formatSaturdayShort(s.date) : "Unknown date"}
                </span>
                <span className="text-amber-800">{s.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Button
        type="button"
        onClick={() => { onClose(); onBackToTop?.(); }}
        data-testid="bulk-result-done"
        className="w-full min-h-[52px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold text-base"
      >
        Back to Sign Ups
      </Button>
    </div>
  );
}

export default function BulkSignupDialog({ open, onClose, board, mySignupShiftIds, onSuccess, onBackToTop }) {
  const { user } = useAuth();
  const [step, setStep] = useState("role"); // role | dates | confirm | result
  const [role, setRole] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setStep("role");
      setRole(null);
      setSelected(new Set());
      setSubmitting(false);
      setResult(null);
    }
  }, [open]);

  const selectedDates = useMemo(() => {
    if (!board?.days || !selected.size) return [];
    const out = [];
    for (const day of board.days) {
      for (const s of day.shifts) {
        if (selected.has(s.id)) out.push(s.date);
      }
    }
    return out;
  }, [board, selected]);

  const submit = async ({ name, email, phone, sms_opt_in }) => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/signups/bulk", {
        role,
        shift_ids: Array.from(selected),
        name, email, phone,
        sms_opt_in: !!sms_opt_in,
      });
      setResult(data);
      setStep("result");
      onSuccess?.();
      if (data.claimed_count > 0) {
        toast.success(`${data.claimed_count} ${data.claimed_count === 1 ? "slot" : "slots"} claimed!`);
      }
      if (data.skipped_count > 0 && data.claimed_count === 0) {
        toast.error("None of the selected slots could be claimed.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't complete bulk sign-up.");
    } finally {
      setSubmitting(false);
    }
  };

  const titles = {
    role: "Step 1 of 3 · Pick a role",
    dates: "Step 2 of 3 · Pick your Saturdays",
    confirm: "Step 3 of 3 · Confirm details",
    result: "All set!",
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="bulk-signup-dialog" className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Sign up for multiple Saturdays</DialogTitle>
          <DialogDescription>{titles[step]}</DialogDescription>
        </DialogHeader>

        {step === "role" && (
          <RoleStep
            board={board}
            mySignupShiftIds={mySignupShiftIds}
            onPick={(r) => { setRole(r); setSelected(new Set()); setStep("dates"); }}
          />
        )}
        {step === "dates" && role && (
          <DateStep
            board={board}
            mySignupShiftIds={mySignupShiftIds}
            role={role}
            selected={selected}
            setSelected={setSelected}
            onBack={() => setStep("role")}
            onNext={() => setStep("confirm")}
          />
        )}
        {step === "confirm" && role && (
          <ConfirmStep
            role={role}
            selectedDates={selectedDates}
            user={user}
            onBack={() => setStep("dates")}
            onSubmit={submit}
            submitting={submitting}
          />
        )}
        {step === "result" && (
          <ResultStep result={result} onClose={onClose} onBackToTop={onBackToTop} />
        )}
      </DialogContent>
    </Dialog>
  );
}
